import { useAllAgorders } from "http-hooks/agorder/useAgorder";
import DeleteAgorder from "components/order/DeleteOrder";
import OrderPrintModal from "components/order/OrderPrintModal";
import { useState } from "react";
import useTableSort, { withSort } from "hooks/useTableSort";
import { formatDateAndTime } from "utils/date";
import { getOrderTypeLabel } from "utils/orderHelpers";
import { SelectVendors, SelectCustomers, SelectAgentsAndSalesmen } from "components/ui/selectors";
import AsyncItemSelect from "components/item/AsyncItemSelect";
import { ActionButton } from "components/ui/Table";
import InputSearch from "components/ui/InputSearch";
import {
  PencilSquareIcon,
  TrashIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import MasterPage, {
  FilterRow,
  FilterCell,
  ClearFiltersButton,
  AddButton,
  DeleteAction,
} from "components/ui/MasterPage";

const initialFilter = {
  ordno: "",
  ordcusid: "",
  ordvendid: "",
  agent_salesman_code: "",
  itemIds: "",
  page: 1,
  limit: 10,
};

const columns = [
  { key: "ordno", header: "Order No.", sortable: true },
  {
    key: "ordate",
    header: "Order Date",
    sortable: true,
    render: (item) => formatDateAndTime(item.ordate),
  },
  {
    key: "ordertyp",
    header: "Order Type",
    sortable: true,
    render: (item) => getOrderTypeLabel(item.ordertyp),
  },
  {
    key: "customer",
    header: "Customer",
    sortable: true,
    render: (item) =>
      item.customer ? (
        <div className="font-medium">{item.customer.name}</div>
      ) : (
        item.ordcusid || "-"
      ),
  },
  {
    key: "vendor",
    header: "Vendor",
    sortable: true,
    render: (item) =>
      item.vendor ? (
        <div className="font-medium">{item.vendor.name}</div>
      ) : (
        item.ordvendid || "-"
      ),
  },
  {
    key: "ordcreated",
    header: "Created At",
    sortable: true,
    render: (item) => formatDateAndTime(item.ordcreated),
  },
  { key: "actions", header: "Actions", isActions: true },
];

export default function Order() {
  const navigate = useNavigate();
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [orderId, setOrderId] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedAgentSalesman, setSelectedAgentSalesman] = useState(null);
  const [filter, setFilter] = useState(initialFilter);
  const [searchOrder, setSearchOrder] = useState("");
  const tableSort = useTableSort();

  const openPrintModal = (id) => {
    setOrderId(id);
    setIsPrintModalOpen(true);
  };

  const handleCustomerChange = (customer) => {
    setSelectedCustomer(customer);
    setFilter((prev) => ({
      ...prev,
      ordcusid: customer?.ps_Id || "",
    }));
  };

  const handleVendorChange = (vendor) => {
    setSelectedVendor(vendor);
    setFilter((prev) => ({
      ...prev,
      ordvendid: vendor?.ps_Id || "",
    }));
  };

  const handleAgentSalesmanChange = (agentSalesman) => {
    setSelectedAgentSalesman(agentSalesman);
    setFilter((prev) => ({
      ...prev,
      agent_salesman_code: agentSalesman?.ps_code || "",
    }));
  };

  const handleItemsChange = (items) => {
    setSelectedItems(items);
    setFilter((prev) => ({
      ...prev,
      itemIds: items.map((item) => item.value).join(","),
    }));
  };

  const handleOrderSearch = (searchTerm) => {
    setFilter((prev) => ({
      ...prev,
      ordno: searchTerm,
    }));
  };

  const clearFilters = () => {
    setSelectedCustomer(null);
    setSelectedVendor(null);
    setSelectedAgentSalesman(null);
    setSelectedItems([]);
    setSearchOrder("");
    setFilter(initialFilter);
  };

  const {
    isLoading,
    data: { data: allorders, page: currentPage, totalPages } = [],
  } = useAllAgorders(withSort(filter, tableSort));

  return (
    <MasterPage
      title="Order"
      headerActions={
        <>
          <AddButton
            variant="secondary"
            onClick={() => navigate("/column-order")}
          >
            Add Column Order
          </AddButton>
          <AddButton onClick={() => navigate("/rate-order")}>
            Add Rate Order
          </AddButton>
        </>
      }
      filters={
        <>
          <FilterRow>
            <FilterCell>
              <InputSearch
                value={searchOrder}
                onChange={(e) => setSearchOrder(e.target.value)}
                onSearch={handleOrderSearch}
                label="Order No."
                placeholder="Order No"
              />
            </FilterCell>
            <FilterCell>
              <SelectCustomers
                value={selectedCustomer}
                onChange={handleCustomerChange}
                label="Customer"
                placeholder="Select Customer"
              />
            </FilterCell>
            <FilterCell>
              <SelectVendors
                value={selectedVendor}
                onChange={handleVendorChange}
                label="Vendor"
                placeholder="Select Vendor"
              />
            </FilterCell>
            <FilterCell>
              <SelectAgentsAndSalesmen
                value={selectedAgentSalesman}
                onChange={handleAgentSalesmanChange}
                label="Agent/Salesman"
                placeholder="Select Agent or Salesman"
              />
            </FilterCell>
          </FilterRow>
          <FilterRow>
            <FilterCell>
              <AsyncItemSelect
                value={selectedItems}
                onChange={handleItemsChange}
                label="Items"
                placeholder="Search items..."
                isMulti={true}
              />
            </FilterCell>
            <ClearFiltersButton onClick={clearFilters} />
          </FilterRow>
        </>
      }
      columns={columns}
      sort={tableSort}
      data={allorders}
      isLoading={isLoading}
      rowKey="orderid"
      deleteComponent={DeleteAgorder}
      renderActions={(order, setDeleteItemId) => (
        <>
          <ActionButton
            variant="secondary"
            onClick={() => openPrintModal(order.orderid)}
            title="View/Print Order"
          >
            <EyeIcon className="h-4 w-4" aria-hidden="true" />
          </ActionButton>
          <ActionButton
            variant="primary"
            onClick={() =>
              navigate(
                `/rate-order?orderId=${order.orderid}&formtype=${order.ordtyp || "G"}`,
                { state: { id: order.orderid } }
              )
            }
            title="Edit as Rate Order"
          >
            <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
          </ActionButton>
          <ActionButton
            variant="success"
            onClick={() =>
              navigate(
                `/column-order?orderId=${order.orderid}&formtype=${order.ordtyp || "G"}`,
                { state: { id: order.orderid } }
              )
            }
            title="Edit as Column Order"
          >
            <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
          </ActionButton>
          <DeleteAction
            onClick={() => setDeleteItemId(order.orderid)}
            title="Delete Order"
          />
        </>
      )}
      extraModals={
        <OrderPrintModal
          isOpen={isPrintModalOpen}
          orderId={orderId}
          onClose={() => setIsPrintModalOpen(false)}
        />
      }
      pagination={{
        currentPage,
        totalPages,
        onPageChange: (page) => setFilter((prev) => ({ ...prev, page })),
      }}
    />
  );
}
